import {
  BaseQuery,
  GROUP_THREADS,
  MessageThread,
  objectToArray,
  PATIENT_THREADS,
  ThreadSearchResult,
  ThreadType,
  toSearchString,
} from '@hb/shared'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useFetchData } from '../../contexts/db/FetchDataContext'
import { PopUpMessageContext } from '../../contexts/PopUpMessage'
import { MessagingWidget } from '../types/MessagingWidget'
import { UseMyThreads } from '../types/UseMyThreads'
import { fillEmptyThread, filterThreads, sortThreadsByMostRecentMessage } from '../utils'

// const ELAN_ID = 'sBCNDAsQGyU31Ie84WlTJopz9HS2'
export const useMyThreads = (
  uid: string | null,
  widget: MessagingWidget,
  playNotificationSound: (() => void) | null,
  threadsView: 'admin' | 'patient',
  getDocumentIdPath: () => any,
): UseMyThreads => {
  const { useQuery } = useFetchData()

  const { data: widgetState, searchQuery, searchMyThreads } = widget
  const myGroupThreadsQuery = useMemo<BaseQuery<MessageThread> | null>(() => {
    if (!uid) return null
    if (threadsView === 'patient') {
      return {
        collection: PATIENT_THREADS,
        filters: [[getDocumentIdPath(), '==', uid]],
      }
    }
    return {
      collection: GROUP_THREADS,
      filters: [['subscribers', 'array-contains', uid]],
    }
  }, [uid, threadsView, getDocumentIdPath])

  // const myDirectMessageThreadsQuery = useMemo(
  //   () => (me
  //     ? query(
  //       DIRECT_MESSAGE_THREADS_REF,
  //       where('subscribers', 'array-contains', me.uid),
  //     )
  //     : null),
  //   [me],
  // )

  const {
    loading: groupThreadsLoading,
    data: groupThreads,
    error: groupThreadsError,
  } = useQuery(myGroupThreadsQuery)

  // const { loading: directMessageThreadsLoading, data: directMessageThreads, error: directMessageThreadsError } = useQuery(myDirectMessageThreadsQuery)

  const totalUnreadThreads = useMemo(() => {
    if (!uid) return 0
    const groupUnreadMessages = groupThreads
      ? objectToArray(groupThreads).reduce(
          (acc, curr) => acc + curr.unreadBy.filter(u => u === uid).length,
          0,
        )
      : 0
    // const directMessageUnreadMessages = directMessageThreads
    //   ? objectToArray(directMessageThreads).reduce(
    //     (acc, curr) => acc + curr.unreadBy.filter((u) => u === me.uid).length,
    //     0,
    //   )
    //   : 0
    return groupUnreadMessages // + directMessageUnreadMessages
  }, [groupThreads, uid])

  const prevMessagesCount = useRef(totalUnreadThreads ?? 0)
  const mounted = useRef(false)
  useEffect(() => {
    if (groupThreadsLoading || !playNotificationSound) {
      mounted.current = false
      return
    }
    if (
      mounted.current &&
      playNotificationSound &&
      totalUnreadThreads > prevMessagesCount.current
    ) {
      playNotificationSound()
    }
    mounted.current = true
    prevMessagesCount.current = totalUnreadThreads
  }, [totalUnreadThreads, playNotificationSound, groupThreadsLoading])

  const { processResponse } = useContext(PopUpMessageContext)

  const [deepSearchLoading, setDeepSearchLoading] = useState(false)
  const [deepSearchResults, setDeepSearchResults] = useState<null | ThreadSearchResult[]>(null)

  useEffect(() => {
    if (!searchQuery) {
      setDeepSearchResults(null)
    }
  }, [searchQuery])
  const deepSearchThreads = useCallback(async () => {
    if (!uid) return
    setDeepSearchLoading(true)
    try {
      await searchMyThreads(searchQuery)
        .then(res => {
          setDeepSearchResults(res)
        })
        .catch(err => {
          processResponse({ error: err.message ?? 'An error occurred' })
          console.error(err)
        })
    } finally {
      setDeepSearchLoading(false)
    }
  }, [uid, searchQuery, processResponse, searchMyThreads])

  return useMemo(() => {
    if (!uid) {
      return {
        data: [],
        selectedTabThreads: [],
        loading: false,
        error: null,
        deepSearchLoading: false,
        deepSearchResults: null,
        searchQuery,
        deepSearchThreads,
        numUnreadThreads: 0,
      }
    }

    const groupThreadsData = objectToArray(groupThreads ?? {}).map(t =>
      fillEmptyThread(uid, ThreadType.GROUP, t.id, t.title ?? '', t),
    )
    // const directMessageThreadsData = objectToArray(
    //   directMessageThreads ?? {},
    // ).map((t) => fillEmptyThread(me, ThreadType.DIRECT, t.id, t.title ?? '', t))

    const sortedThreads = sortThreadsByMostRecentMessage([
      ...groupThreadsData,
      // ...directMessageThreadsData,
    ])
    const selectedTabThreads = searchQuery
      ? sortedThreads.filter(t =>
          t.thread.searchString.toLowerCase().includes(toSearchString(searchQuery)),
        )
      : filterThreads(sortedThreads, widgetState?.tab)
    return {
      data: sortedThreads,
      selectedTabThreads,
      deepSearchLoading,
      deepSearchResults,
      loading: groupThreadsLoading,
      searchQuery,
      deepSearchThreads,
      // || directMessageThreadsLoading,
      error: groupThreadsError, // || directMessageThreadsError,
      numUnreadThreads: totalUnreadThreads,
    }
  }, [
    groupThreadsLoading,
    // directMessageThreadsLoading,
    searchQuery,
    deepSearchLoading,
    deepSearchResults,
    totalUnreadThreads,
    deepSearchThreads,
    // directMessageThreads,
    groupThreads,
    uid,
    // directMessageThreadsError,
    groupThreadsError,
    widgetState,
  ])
}
